import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type MouseEvent, type RefObject } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { addDays, isIsoDate } from "@talentkontor/domain";
import { api } from "../../data";
import { Dialog, useAction, useRueckfrage } from "../../components/ui";
import { date, today } from "../../lib/format";
import { t } from "../../lib/sprache";
import { tagLang } from "../kommunikation/kalender/model";
import { Fehler, Laden } from "./parts";
import { useLeads, useIch } from "./crm-teile";
import { istBetrieb } from "./konsole-nav";
import { useKonsolenRolle } from "./rolle";
import {
  ALLE_GRUPPEN, GRUPPEN, FEED_KEY, ZONE, aboAdressen, aboBeenden, aboErzeugen, aboStand, abwesenheitLabel, alsGanztag, amTag, artLabel, bereich,
  blaettern, eventId, filtere, formularAus, heute as heuteListen, istEigener, kopfTitel, kundenfristen, ladeFeed, lagen, legeZeitfensterAn, leeresFormular,
  loescheEintrag, MEHR_PX, personFarbe, personName, pruefe, speichereEintrag, stunden, titel, titelZeilen, verteile, wochentagKurz, zeitText, ziel,
  type Ansicht, type Feed, type FormArt, type Formular, type Gruppe, type KalEintrag, type Sicht,
} from "./kalender-model";
import "../kommunikation/kalender/kalender.css";
import "./kalender.css";

/**
 * Team-Kalender der Betreiber-Konsole (/intern/kalender, Plan konsole-kalender.md, Migration 0530). Ansichten Tag, Woche und
 * Monat, „Mein Kalender“ oder „Team“ mit einer Farbe je Person, Filter nach Art. Eigene Einträge öffnen den Dialog, automatische
 * (Demo, Aufgabe, Frist) führen direkt zu Lead, Konto oder Demo. Neuer Termin per Klick in die Zeit; Abwesenheit als
 * ganztägiger Block. Bausteine des Agentur-Kalenders (Monatsraster, Tagesnamen, Klassen tk-cal-*) werden übernommen.
 * Sprunglinks: ?ansicht=tag|woche|monat, ?datum=YYYY-MM-DD, ?sicht=mein|team, ?arten=termine,demo …
 */

const STUNDE_PX = 48;
const SCHMAL = "(max-width: 760px)";
function useSchmal(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => undefined;
      const mq = window.matchMedia(SCHMAL);
      mq.addEventListener("change", cb);
      return () => mq.removeEventListener("change", cb);
    },
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(SCHMAL).matches,
    () => false,
  );
}

type DialogZustand = { art: "neu"; formular: Formular } | { art: "bearbeiten"; e: KalEintrag } | { art: "lesen"; e: KalEintrag } | { art: "abo" }
  | { art: "liste"; titel: string; eintraege: KalEintrag[] };

/** Breite einer Tagesspalte des Zeitrasters (px), nachgemessen bei jeder Größenänderung; ohne Messung (Server, Tests) null */
function useSpaltenBreite(): [RefObject<HTMLDivElement | null>, number | null] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [breite, setBreite] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const messen = () => {
      const w = el.querySelector(".tk-kal-spalte")?.getBoundingClientRect().width;
      if (w) setBreite(Math.round(w));
    };
    messen();
    const ro = new ResizeObserver(messen);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, breite];
}

export function KonsoleKalender() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const ich = useIch() ?? "";
  const schmal = useSchmal();
  const heuteTag = today(ZONE);
  const datum = isIsoDate(params.get("datum") ?? "") ? params.get("datum")! : heuteTag;
  const ansicht: Ansicht = (["tag", "woche", "monat"] as const).find((a) => a === params.get("ansicht")) ?? "woche";
  const sicht: Sicht = params.get("sicht") === "team" ? "team" : "mein";
  const arten = (params.get("arten")?.split(",").filter((x): x is Gruppe => (ALLE_GRUPPEN as string[]).includes(x))) ?? [...ALLE_GRUPPEN];
  const [ohnePersonen, setOhnePersonen] = useState<string[]>([]);
  const [dialog, setDialog] = useState<DialogZustand | null>(null);
  const setze = (werte: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(werte)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const b = bereich(ansicht, datum);
  const q = useQuery({ queryKey: [FEED_KEY, b.von, b.bis, sicht], queryFn: () => ladeFeed(api, b.von, b.bis, sicht), placeholderData: keepPreviousData });
  const feed = q.data;
  const sichtbar = useMemo(() => {
    if (!feed) return [] as KalEintrag[];
    const personen = sicht === "team" && ohnePersonen.length ? feed.personen.map((p) => p.user_id).filter((id) => !ohnePersonen.includes(id)) : null;
    return filtere(feed.eintraege, arten, personen);
  }, [feed, arten, sicht, ohnePersonen]);

  const oeffne = (e: KalEintrag) => {
    if (istEigener(e) && e.editable) return setDialog({ art: "bearbeiten", e });
    if (istEigener(e) || e.belegt) return setDialog({ art: "lesen", e });
    const z = ziel(e);
    if (z) navigate(z.to);
    else setDialog({ art: "lesen", e });
  };
  const neu = (tag: string, zeit: string | null, kind: FormArt = "termin") => setDialog({ art: "neu", formular: leeresFormular(tag, zeit, ich, kind) });
  const schalteArt = (g: Gruppe) => {
    const next = arten.includes(g) ? arten.filter((x) => x !== g) : [...arten, g];
    setze({ arten: next.length === ALLE_GRUPPEN.length ? null : next.join(",") || "keine" });
  };

  return (
    <>
      <title>{t("Kalender · Konsole · CreatorOffice", "Calendar · Console · CreatorOffice")}</title>
      <div className="tk-demo-kopf">
        <div>
          <h1>{t("Kalender", "Calendar")}</h1>
          <p className="muted">{t("Demos, Vertriebsaufgaben, Kundenfristen und eigene Termine. Alle Zeiten in deutscher Zeit (Europe/Berlin).",
            "Demos, sales tasks, customer deadlines and your own appointments. All times in German time (Europe/Berlin).")}</p>
        </div>
        <div className="row tk-kal-aktionen">
          <button type="button" onClick={() => setDialog({ art: "abo" })}>{t("Kalender abonnieren", "Subscribe to calendar")}</button>
          <button type="button" onClick={() => neu(datum, null, "abwesenheit")}>{t("Abwesenheit eintragen", "Add time off")}</button>
          <button type="button" className="primary" onClick={() => neu(datum, null)}>{t("Neuer Termin", "New appointment")}</button>
        </div>
      </div>

      <div className="row between tk-kal-leiste">
        <div className="row tk-kal-blaettern">
          <button type="button" aria-label={t("Zurück", "Previous")} onClick={() => setze({ datum: blaettern(ansicht, datum, -1) })}>‹</button>
          <button type="button" onClick={() => setze({ datum: null })}>{t("Heute", "Today")}</button>
          <button type="button" aria-label={t("Weiter", "Next")} onClick={() => setze({ datum: blaettern(ansicht, datum, 1) })}>›</button>
          <h2 className="tk-kal-titel" aria-live="polite">{kopfTitel(ansicht, datum)}</h2>
        </div>
        <div className="row tk-kal-schalter">
          <div className="tk-kal-segment" role="group" aria-label={t("Ansicht", "View")}>
            {([["tag", t("Tag", "Day")], ["woche", t("Woche", "Week")], ["monat", t("Monat", "Month")]] as const).map(([k, l]) => (
              <button key={k} type="button" aria-pressed={ansicht === k} className={ansicht === k ? "primary" : ""} onClick={() => setze({ ansicht: k === "woche" ? null : k })}>{l}</button>
            ))}
          </div>
          <div className="tk-kal-segment" role="group" aria-label={t("Kalender", "Calendar")}>
            <button type="button" aria-pressed={sicht === "mein"} className={sicht === "mein" ? "primary" : ""} onClick={() => setze({ sicht: null })}>{t("Mein Kalender", "My calendar")}</button>
            <button type="button" aria-pressed={sicht === "team"} className={sicht === "team" ? "primary" : ""} onClick={() => setze({ sicht: "team" })}>{t("Team", "Team")}</button>
          </div>
        </div>
      </div>

      <div className="toolbar tk-kal-filter" role="group" aria-label={t("Nach Art filtern", "Filter by type")}>
        {GRUPPEN.map((g) => <button key={g.key} type="button" aria-pressed={arten.includes(g.key)} onClick={() => schalteArt(g.key)}>{g.label}</button>)}
      </div>

      {sicht === "team" && feed && (
        <div className="tk-kal-legende" role="group" aria-label={t("Personen", "People")}>
          {feed.personen.filter((p) => p.active).map((p) => (
            <button key={p.user_id} type="button" aria-pressed={!ohnePersonen.includes(p.user_id)}
              onClick={() => setOhnePersonen((x) => (x.includes(p.user_id) ? x.filter((y) => y !== p.user_id) : [...x, p.user_id]))}>
              <i className="tk-cal-punkt" style={{ background: personFarbe(feed.personen, p.user_id) }} aria-hidden="true" />
              {p.name}{p.me ? t(" (ich)", " (me)") : ""}
            </button>
          ))}
        </div>
      )}

      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !feed ? <Laden /> : (
        <div aria-busy={q.isFetching || undefined}>
          {ansicht === "monat"
            ? <MonatsAnsicht feed={feed} liste={sichtbar} tage={b.tage} datum={datum} heute={heuteTag} schmal={schmal} onOpen={oeffne}
                onTag={(tag) => (schmal ? setze({ datum: tag }) : setze({ ansicht: "tag", datum: tag }))} onNeu={neu} />
            : ansicht === "woche" && schmal
              ? <Agenda feed={feed} liste={sichtbar} tage={b.tage} heute={heuteTag} onOpen={oeffne} onNeu={neu} />
              : <ZeitRaster feed={feed} liste={sichtbar} tage={b.tage} heute={heuteTag} onOpen={oeffne} onNeu={neu}
                  onTag={(tag) => setze({ ansicht: "tag", datum: tag })} onListe={(titel, eintraege) => setDialog({ art: "liste", titel, eintraege })} />}
        </div>
      )}

      {feed && <KalenderDialoge zustand={dialog} setZustand={setDialog} feed={feed} ich={ich} onOpen={oeffne} />}
    </>
  );
}

// ── Ansichten ─────────────────────────────────────────────────────────────────

function Punkt({ farbe }: { farbe: string }) {
  return <i className="tk-cal-punkt" style={{ background: farbe }} aria-hidden="true" />;
}

function hinweis(e: KalEintrag, feed: Feed): string {
  const wer = e.user_id ? personName(feed.personen, e.user_id) : e.note === "an den Admin" ? t("an den Admin", "for the admin") : t("ohne Zuständigen", "unassigned");
  return `${titel(e)} · ${zeitText(e)} · ${wer}`;
}

/** Ganztägiger Eintrag oder Eintrag im Monatsraster: Fläche in der Farbe der Person, Rand neutral rundum */
function Chip({ e, feed, onOpen }: { e: KalEintrag; feed: Feed; onOpen: (e: KalEintrag) => void }) {
  const farbe = personFarbe(feed.personen, e.user_id);
  return (
    <button type="button" className={`tk-kal-chip${e.belegt ? " tk-kal-belegt" : ""}`} style={{ ["--tk-kal-farbe" as string]: farbe }}
      onClick={() => onOpen(e)} title={hinweis(e, feed)} aria-label={`${hinweis(e, feed)} · ${t("öffnen", "open")}`}>
      <Punkt farbe={farbe} />
      {!e.all_day && e.start_time ? <span className="num">{e.start_time}</span> : null}
      <span className="tk-kal-chip-text">{titel(e)}</span>
    </button>
  );
}

const weitere = (n: number) => (n === 1 ? t("1 weiterer Termin gleichzeitig", "1 more appointment at the same time")
  : t(`${n} weitere Termine gleichzeitig`, `${n} more appointments at the same time`));
const uhr = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function ZeitRaster({ feed, liste, tage, heute, onOpen, onNeu, onTag, onListe }: {
  feed: Feed; liste: KalEintrag[]; tage: string[]; heute: string; onOpen: (e: KalEintrag) => void;
  onNeu: (tag: string, zeit: string | null) => void; onTag: (tag: string) => void; onListe: (titel: string, eintraege: KalEintrag[]) => void;
}) {
  const [ref, breite] = useSpaltenBreite();
  const woche = tage.length > 1;
  const jeTag = tage.map((tag) => ({ tag, ganz: amTag(liste, tag).filter((e) => alsGanztag(e, tag)), lagen: lagen(liste, tag) }));
  const { von, bis } = stunden(jeTag.flatMap((x) => x.lagen));
  const hoehe = (bis - von) * STUNDE_PX;
  const neuBeiKlick = (tag: string, h: number) => (ev: MouseEvent<HTMLButtonElement>) => {
    const halb = (ev.nativeEvent as globalThis.MouseEvent).offsetY > STUNDE_PX / 2;
    onNeu(tag, `${String(h).padStart(2, "0")}:${halb ? "30" : "00"}`);
  };
  const jetzt = new Intl.DateTimeFormat("de-DE", { timeZone: ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
  const jetztMin = Number(jetzt.slice(0, 2)) * 60 + Number(jetzt.slice(3, 5));
  return (
    <div ref={ref} className="tk-kal-raster" style={{ ["--tk-kal-tage" as string]: tage.length }} role="group" aria-label={t("Zeitraster", "Time grid")}>
      <div className="tk-kal-kopfzeile">
        <span />
        {tage.map((tag) => (
          <button key={tag} type="button" className={`tk-kal-tagkopf${tag === heute ? " tk-kal-heute" : ""}`} onClick={() => onTag(tag)}
            aria-label={`${tagLang(tag)}${tag === heute ? t(" (heute)", " (today)") : ""}`}>
            <small>{wochentagKurz(tag)}</small> <strong>{Number(tag.slice(8, 10))}</strong>
          </button>
        ))}
      </div>
      <div className="tk-kal-ganztag">
        <small className="muted">{t("ganztägig", "all day")}</small>
        {jeTag.map(({ tag, ganz }) => (
          <div key={tag} className="tk-kal-ganztag-tag">{ganz.map((e) => <Chip key={`${e.key}-${tag}`} e={e} feed={feed} onOpen={onOpen} />)}</div>
        ))}
      </div>
      <div className="tk-kal-zeiten" style={{ height: hoehe }}>
        <div className="tk-kal-stunden" aria-hidden="true">
          {Array.from({ length: bis - von }, (_, i) => <span key={i} style={{ top: i * STUNDE_PX }}>{String(von + i).padStart(2, "0")}:00</span>)}
        </div>
        {jeTag.map(({ tag, lagen: alle }) => {
          // Praxistest 04.11.2026: gleichzeitige Termine mit Mindestbreite, in der Woche höchstens zwei nebeneinander, sonst „+n“
          const { sichtbar: ls, mehr } = verteile(alle, breite, woche);
          return (
          <div key={tag} className={`tk-kal-spalte${tag === heute ? " tk-kal-heute" : ""}`}>
            {Array.from({ length: bis - von }, (_, i) => (
              <button key={i} type="button" className="tk-kal-slot" style={{ top: i * STUNDE_PX, height: STUNDE_PX }} onClick={neuBeiKlick(tag, von + i)}
                aria-label={`${t("Neuer Termin", "New appointment")} · ${tagLang(tag)} · ${String(von + i).padStart(2, "0")}:00`} />
            ))}
            {tag === heute && jetztMin >= von * 60 && jetztMin <= bis * 60 && (
              <i className="tk-kal-jetzt" style={{ top: ((jetztMin - von * 60) * STUNDE_PX) / 60 }} aria-hidden="true" />
            )}
            {ls.map((l) => {
              const farbe = personFarbe(feed.personen, l.e.user_id);
              const top = ((l.von - von * 60) * STUNDE_PX) / 60;
              const h = ((l.ende - l.von) * STUNDE_PX) / 60;
              const rest = l.mitMehr ? `(100% - ${MEHR_PX}px)` : "100%";
              return (
                <button key={l.e.key} type="button" className={`tk-kal-block${l.e.belegt ? " tk-kal-belegt" : ""}`} onClick={() => onOpen(l.e)}
                  title={hinweis(l.e, feed)} aria-label={`${hinweis(l.e, feed)} · ${t("öffnen", "open")}`}
                  style={{ top, height: h, ["--tk-kal-h" as string]: `${h}px`, ["--tk-kal-zeilen" as string]: titelZeilen(h),
                    left: `calc(${rest} * ${l.spalte / l.spalten} + 2px)`, width: `calc(${rest} / ${l.spalten} - 4px)`, ["--tk-kal-farbe" as string]: farbe }}>
                  {/* Uhrzeit und Titel als ein Fließtext: der Titel beginnt in der ersten Zeile und bricht danach um */}
                  <span className="tk-kal-block-text">
                    <Punkt farbe={farbe} /> <span className="num tk-kal-block-zeit">{l.e.start_time}</span> <span className="tk-kal-block-titel">{titel(l.e)}</span>
                  </span>
                </button>
              );
            })}
            {mehr.map((m) => {
              const top = ((m.von - von * 60) * STUNDE_PX) / 60;
              const h = ((m.ende - m.von) * STUNDE_PX) / 60;
              const zeit = `${uhr(m.zeitVon)}–${uhr(m.zeitBis)}`;
              const titelListe = `${tagLang(tag)} · ${zeit}`;
              return (
                <button key={m.key} type="button" className="tk-kal-mehr" style={{ top, height: h, width: MEHR_PX - 2 }}
                  onClick={() => onListe(titelListe, m.eintraege)}
                  title={`${weitere(m.anzahl)} · ${zeit}`}
                  aria-label={`${weitere(m.anzahl)}, ${tagLang(tag)}, ${zeit} · ${t("Liste öffnen", "open list")}`}>
                  +{m.anzahl}
                </button>
              );
            })}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function MonatsAnsicht({ feed, liste, tage, datum, heute, schmal, onOpen, onTag, onNeu }: {
  feed: Feed; liste: KalEintrag[]; tage: string[]; datum: string; heute: string; schmal: boolean;
  onOpen: (e: KalEintrag) => void; onTag: (tag: string) => void; onNeu: (tag: string, zeit: string | null) => void;
}) {
  const monat = datum.slice(0, 7);
  const kopf = tage.slice(0, 7);
  const gewaehlt = amTag(liste, datum);
  return (
    <>
      <div className="calendar tk-kal-monat" role="group" aria-label={kopfTitel("monat", datum)}>
        {kopf.map((tag) => <div key={`k-${tag}`} className="day tk-cal-kopf"><strong>{wochentagKurz(tag)}</strong></div>)}
        {tage.map((tag) => {
          const es = amTag(liste, tag);
          const zeigen = es.slice(0, 3);
          const fremd = tag.slice(0, 7) !== monat;
          return (
            <div key={tag} className={`day${tag === heute ? " tk-cal-heute" : ""}${schmal && tag === datum ? " tk-cal-gewaehlt" : ""}${fremd ? " tk-kal-fremd" : ""}`}>
              <strong>
                <button type="button" className="tk-cal-tag" onClick={() => onTag(tag)} aria-pressed={schmal ? tag === datum : undefined}
                  aria-label={`${tagLang(tag)}${tag === heute ? t(" (heute)", " (today)") : ""}${es.length ? ` · ${es.length === 1 ? t("1 Eintrag", "1 entry") : t(`${es.length} Einträge`, `${es.length} entries`)}` : ""}`}>
                  {Number(tag.slice(8, 10))}
                </button>
              </strong>
              {schmal
                ? <span className="tk-kal-punkte" aria-hidden="true">{es.slice(0, 4).map((e) => <Punkt key={e.key} farbe={personFarbe(feed.personen, e.user_id)} />)}</span>
                : <>
                    {zeigen.map((e) => <Chip key={`${e.key}-${tag}`} e={e} feed={feed} onOpen={onOpen} />)}
                    {es.length > zeigen.length && <button type="button" className="tk-cal-mehr" onClick={() => onTag(tag)}>+{es.length - zeigen.length} {t("weitere", "more")}</button>}
                  </>}
            </div>
          );
        })}
      </div>
      {schmal && (
        <section className="panel stack" aria-labelledby="kal-tag-h">
          <div className="row between">
            <h3 id="kal-tag-h">{tagLang(datum)}</h3>
            <button type="button" onClick={() => onNeu(datum, null)}>{t("Termin an diesem Tag", "Appointment on this day")}</button>
          </div>
          {gewaehlt.length ? gewaehlt.map((e) => <Zeile key={e.key} e={e} feed={feed} onOpen={onOpen} />) : <p className="muted">{t("An diesem Tag gibt es keine Einträge.", "There are no entries on this day.")}</p>}
        </section>
      )}
    </>
  );
}

function Zeile({ e, feed, onOpen }: { e: KalEintrag; feed: Feed; onOpen: (e: KalEintrag) => void }) {
  return (
    <div className="task">
      <div>
        <strong>
          <button type="button" className="tk-cal-link tk-kal-zeilenlink" onClick={() => onOpen(e)} title={hinweis(e, feed)}>
            <Punkt farbe={personFarbe(feed.personen, e.user_id)} /> {titel(e)}
          </button>
        </strong>
        <p className="muted">{zeitText(e)} · {e.user_id ? personName(feed.personen, e.user_id) : t("ohne Person", "no person")}</p>
      </div>
      <span className={`pill${e.belegt ? "" : e.gruppe === "fristen" ? " warning" : e.gruppe === "demo" ? " info" : e.gruppe === "abwesenheit" ? "" : " success"}`}>{e.belegt && e.art !== "abwesenheit" ? t("Privat", "Private") : artLabel(e.art)}</span>
    </div>
  );
}

/** Woche am Handy: Liste nach Tagen statt sieben schmaler Spalten */
function Agenda({ feed, liste, tage, heute, onOpen, onNeu }: { feed: Feed; liste: KalEintrag[]; tage: string[]; heute: string; onOpen: (e: KalEintrag) => void; onNeu: (tag: string, zeit: string | null) => void }) {
  return (
    <section className="panel stack tk-kal-agenda" aria-label={t("Woche als Liste", "Week as list")}>
      {tage.map((tag) => {
        const es = amTag(liste, tag);
        return (
          <div key={tag}>
            <div className="row between">
              <div className="group-label">{tagLang(tag)}{tag === heute ? t(" · heute", " · today") : ""}</div>
              <button type="button" className="tk-cal-mehr" onClick={() => onNeu(tag, null)} aria-label={`${t("Neuer Termin", "New appointment")} · ${tagLang(tag)}`}>+ {t("Termin", "Appointment")}</button>
            </div>
            {es.length ? es.map((e) => <Zeile key={`${e.key}-${tag}`} e={e} feed={feed} onOpen={onOpen} />) : <p className="muted">{t("Keine Einträge.", "No entries.")}</p>}
          </div>
        );
      })}
    </section>
  );
}

// ── Dialoge ───────────────────────────────────────────────────────────────────

function KalenderDialoge({ zustand, setZustand, feed, ich, onOpen }: { zustand: DialogZustand | null; setZustand: (z: DialogZustand | null) => void; feed: Feed; ich: string; onOpen: (e: KalEintrag) => void }) {
  const zu = () => setZustand(null);
  const titelText = !zustand ? "" : zustand.art === "abo" ? t("Kalender abonnieren", "Subscribe to calendar") : zustand.art === "liste" ? zustand.titel
    : zustand.art === "neu" ? (zustand.formular.kind === "abwesenheit" ? t("Abwesenheit eintragen", "Add time off") : t("Neuer Termin", "New appointment"))
    : zustand.art === "bearbeiten" ? t("Eintrag bearbeiten", "Edit entry") : titel(zustand.e);
  return (
    <Dialog open={!!zustand} onClose={zu} title={titelText}>
      {zustand?.art === "abo" && <Abo />}
      {zustand?.art === "liste" && (
        <div className="stack tk-kal-liste">
          {zustand.eintraege.map((e) => <Zeile key={e.key} e={e} feed={feed} onOpen={onOpen} />)}
        </div>
      )}
      {zustand?.art === "lesen" && <Lesen e={zustand.e} feed={feed} />}
      {(zustand?.art === "neu" || zustand?.art === "bearbeiten") && (
        <EintragFormular key={zustand.art === "neu" ? "neu" : zustand.e.key} start={zustand.art === "neu" ? zustand.formular : formularAus(zustand.e)}
          id={zustand.art === "bearbeiten" ? eventId(zustand.e) : null} feed={feed} ich={ich} onDone={zu} />
      )}
    </Dialog>
  );
}

function Lesen({ e, feed }: { e: KalEintrag; feed: Feed }) {
  const z = ziel(e);
  return (
    <div className="stack">
      <div className="tk-cal-zeilen">
        <div className="tk-cal-zeile"><strong>{t("Art", "Type")}</strong><span>{e.belegt && e.art !== "abwesenheit" ? t("Privat", "Private") : artLabel(e.art)}</span></div>
        <div className="tk-cal-zeile"><strong>{t("Zeit", "Time")}</strong><span>{`${date(e.starts_on)}, ${zeitText(e)}`}</span></div>
        <div className="tk-cal-zeile"><strong>{t("Person", "Person")}</strong><span><Punkt farbe={personFarbe(feed.personen, e.user_id)} /> {personName(feed.personen, e.user_id)}</span></div>
        {e.location && <div className="tk-cal-zeile"><strong>{t("Ort", "Location")}</strong><span>{e.location}</span></div>}
        {e.meeting_url && <div className="tk-cal-zeile"><strong>Link</strong><span><a href={e.meeting_url} target="_blank" rel="noopener noreferrer">{e.meeting_url}</a></span></div>}
        {e.note && <div className="tk-cal-zeile"><strong>{t("Notiz", "Note")}</strong><span>{e.note}</span></div>}
      </div>
      {e.belegt && <p className="muted">{t("Privater Eintrag: Inhalt sieht nur die Person selbst.", "Private entry: only the person sees its content.")}</p>}
      {!e.belegt && !e.editable && istEigener(e) && <p className="muted">{t("Ändern können die Person selbst, Admins und die Entwicklung.", "The person, admins and development can change it.")}</p>}
      {z && <div className="row"><Link className="btn primary" to={z.to}>{z.label}</Link></div>}
    </div>
  );
}

function EintragFormular({ start, id, feed, ich, onDone }: { start: Formular; id: string | null; feed: Feed; ich: string; onDone: () => void }) {
  const [f, setF] = useState<Formular>(start);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const { busy, error, run } = useAction();
  const { ask, element } = useRueckfrage();
  const leads = useLeads();
  const set = (p: Partial<Formular>) => setF((x) => ({ ...x, ...p }));
  const fremd = !!f.user_id && f.user_id !== ich;
  const ganz = f.kind === "abwesenheit" || (f.kind !== "zeitfenster" && f.all_day);
  const arten: { key: FormArt; label: string }[] = [
    { key: "termin", label: t("Termin", "Appointment") }, { key: "call", label: "Call" }, { key: "abwesenheit", label: t("Abwesenheit", "Time off") },
    ...(feed.darf_zeitfenster && !id ? [{ key: "zeitfenster" as const, label: t("Demo-Zeitfenster", "Demo slot") }] : []),
  ];
  const speichern = async (ev: FormEvent) => {
    ev.preventDefault();
    const r = pruefe(f);
    setFehler(r.fehler);
    if (!r.daten) return;
    const ok = await run("speichern", async () => {
      if (f.kind === "zeitfenster") await legeZeitfensterAn(api, r.daten!);
      else await speichereEintrag(api, id, r.daten!);
      return true;
    }, f.kind === "zeitfenster" ? t("Demo-Zeitfenster freigegeben.", "Demo slot opened.") : t("Gespeichert.", "Saved."));
    if (ok) onDone();
  };
  const loeschen = async () => {
    if (!id || !(await ask({ title: t("Eintrag löschen?", "Delete entry?"), text: t("Der Eintrag verschwindet aus allen Kalendern und Abos.", "The entry disappears from all calendars and subscriptions."), confirmLabel: t("Löschen", "Delete"), danger: true }))) return;
    const ok = await run("loeschen", async () => { await loescheEintrag(api, id); return true; }, t("Gelöscht.", "Deleted."));
    if (ok) onDone();
  };
  const feld = (k: string) => (fehler[k] ? { "aria-invalid": true as const, "aria-describedby": `kal-f-${k}` } : {});
  const meldung = (k: string) => (fehler[k] ? <small id={`kal-f-${k}`} className="tk-kal-fehler">{fehler[k]}</small> : null);
  const personen = feed.personen.filter((p) => p.active || p.user_id === f.user_id);
  return (
    <form className="stack" onSubmit={(ev) => void speichern(ev)} noValidate>
      {!id && (
        <div className="tk-kal-segment" role="group" aria-label={t("Art", "Type")}>
          {arten.map((a) => (
            <button key={a.key} type="button" aria-pressed={f.kind === a.key} className={f.kind === a.key ? "primary" : ""}
              onClick={() => set({ kind: a.key, ...(a.key === "abwesenheit" ? { all_day: true } : {}) })}>{a.label}</button>
          ))}
        </div>
      )}
      {f.kind === "abwesenheit" && (
        <label>{t("Grund", "Reason")}
          <select value={f.absence_kind} onChange={(e) => set({ absence_kind: e.target.value as Formular["absence_kind"] })}>
            {(["urlaub", "krank", "sonstiges"] as const).map((k) => <option key={k} value={k}>{abwesenheitLabel(k)}</option>)}
          </select>
        </label>
      )}
      {f.kind !== "zeitfenster" && (
        <label>{f.kind === "abwesenheit" ? t("Titel (optional)", "Title (optional)") : t("Titel", "Title")}
          <input value={f.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} {...feld("title")}
            placeholder={f.kind === "abwesenheit" ? abwesenheitLabel(f.absence_kind) : f.kind === "call" ? t("z. B. Erstgespräch Studio Nord", "e.g. intro call Studio Nord") : ""} />
          {meldung("title")}
        </label>
      )}
      {f.kind !== "zeitfenster" && f.kind !== "abwesenheit" && (
        <label className="row tk-kal-check"><input type="checkbox" checked={f.all_day} onChange={(e) => set({ all_day: e.target.checked })} /> {t("Ganztägig", "All day")}</label>
      )}
      <div className="grid two tk-kal-felder">
        <label>{ganz ? t("Von", "From") : t("Datum", "Date")}
          <input type="date" value={f.start_date} onChange={(e) => set({ start_date: e.target.value, ...(f.end_date < e.target.value ? { end_date: e.target.value } : {}) })} {...feld("start_date")} />
          {meldung("start_date")}
        </label>
        {ganz ? (
          <label>{t("Bis (einschließlich)", "Until (inclusive)")}
            <input type="date" value={f.end_date} min={f.start_date} onChange={(e) => set({ end_date: e.target.value })} {...feld("end_date")} />
            {meldung("end_date")}
          </label>
        ) : (
          <label>{t("Beginn", "Start")}
            <input type="time" step={900} value={f.start_time} onChange={(e) => set({ start_time: e.target.value })} {...feld("start_time")} />
            {meldung("start_time")}
          </label>
        )}
        {!ganz && f.kind !== "zeitfenster" && (
          <>
            <label>{t("Ende", "End")}
              <input type="time" step={900} value={f.end_time} onChange={(e) => set({ end_time: e.target.value })} {...feld("end_time")} />
              {meldung("end_time")}
            </label>
            <label>{t("Endet am", "Ends on")}
              <input type="date" value={f.end_date} min={f.start_date} onChange={(e) => set({ end_date: e.target.value })} {...feld("end_date")} />
              {meldung("end_date")}
            </label>
          </>
        )}
        {f.kind === "zeitfenster" && (
          <label>{t("Dauer (Minuten)", "Duration (minutes)")}
            <select value={f.minutes} onChange={(e) => set({ minutes: e.target.value })} {...feld("minutes")}>
              {["15", "30", "45", "60", "90"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {meldung("minutes")}
          </label>
        )}
      </div>
      {f.kind !== "zeitfenster" && feed.darf_fremde && (
        <label>{t("Person", "Person")}
          <select value={f.user_id} onChange={(e) => set({ user_id: e.target.value, ...(e.target.value !== ich ? { private: false } : {}) })}>
            {personen.map((p) => <option key={p.user_id} value={p.user_id} disabled={!p.active}>{p.name}{p.me ? t(" (ich)", " (me)") : ""}</option>)}
          </select>
        </label>
      )}
      {f.kind !== "zeitfenster" && (
        <label className="row tk-kal-check">
          <input type="checkbox" checked={f.private} disabled={fremd} onChange={(e) => set({ private: e.target.checked })} />
          {t("Privat: andere sehen nur „belegt“", "Private: others only see “busy”")}
        </label>
      )}
      {(f.kind === "termin" || f.kind === "call") && (
        <label>{t("Bezug (Lead oder Konto)", "Related lead or account")}
          <select value={f.lead_id} onChange={(e) => set({ lead_id: e.target.value })} disabled={!leads.data}>
            <option value="">{t("ohne Bezug", "none")}</option>
            {(leads.data ?? []).slice().sort((a, b) => (a.company ?? a.contact_name ?? "").localeCompare(b.company ?? b.contact_name ?? ""))
              .map((l) => <option key={l.id} value={l.id}>{l.company ?? l.contact_name ?? l.email}</option>)}
          </select>
        </label>
      )}
      {(f.kind === "termin" || f.kind === "call") && (
        <label>{t("Ort", "Location")}<input value={f.location} maxLength={300} onChange={(e) => set({ location: e.target.value })} /></label>
      )}
      {f.kind !== "abwesenheit" && (
        <label>{t("Link (Video-Call)", "Link (video call)")}
          <input type="url" inputMode="url" placeholder="https://" value={f.meeting_url} onChange={(e) => set({ meeting_url: e.target.value })} {...feld("meeting_url")} />
          {meldung("meeting_url")}
        </label>
      )}
      {f.kind !== "zeitfenster" && (
        <label>{t("Notiz", "Note")}<textarea rows={3} maxLength={4000} value={f.note} onChange={(e) => set({ note: e.target.value })} /></label>
      )}
      {f.kind === "zeitfenster" && <p className="muted">{t("Das Zeitfenster erscheint auf creatoroffice.de/demo zum Buchen. Termine verschickt der Kalender nicht.", "The slot appears on creatoroffice.de/demo for booking. The calendar does not send invitations.")}</p>}
      {error && <div className="notice error" role="alert">{error}</div>}
      <div className="row between">
        <div className="row">
          <button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "speichern"}>{t("Speichern", "Save")}</button>
          <button type="button" onClick={onDone}>{t("Abbrechen", "Cancel")}</button>
        </div>
        {id && <button type="button" className="danger" disabled={!!busy} onClick={() => void loeschen()}>{t("Löschen", "Delete")}</button>}
      </div>
      {element}
    </form>
  );
}

function Abo() {
  const stand = useQuery({ queryKey: ["staff_calendar_link_status"], queryFn: () => aboStand(api) });
  const { busy, run } = useAction();
  const [token, setToken] = useState<string | null>(null);
  const urls = token ? aboAdressen(token, import.meta.env.VITE_SUPABASE_URL as string | undefined) : null;
  const erzeugen = () => void run("abo", async () => setToken(await aboErzeugen(api)),
    stand.data ? t("Neuer Link erzeugt. Der alte gilt nicht mehr.", "New link created. The old one no longer works.") : t("Link erzeugt.", "Link created."));
  return (
    <div className="stack">
      <p>{t("Mit dem Abo erscheint der Kalender in Google Kalender, Apple Kalender oder Outlook. Es gibt zwei Adressen: dein eigener Kalender mit allen Details und der Team-Kalender, in dem private Einträge nur als „belegt“ stehen. Der Link ist geheim und nur für dich.",
        "With the subscription the calendar appears in Google Calendar, Apple Calendar or Outlook. There are two addresses: your own calendar with all details and the team calendar, where private entries only show as “busy”. The link is secret and only for you.")}</p>
      {stand.data && !token && (
        <p className="muted">{t("Abo aktiv seit", "Subscription active since")} {date(stand.data.created_at)}{stand.data.last_used_at ? t(`, zuletzt abgerufen am ${date(stand.data.last_used_at)}`, `, last fetched on ${date(stand.data.last_used_at)}`) : t(", noch nicht abgerufen", ", not fetched yet")}.</p>
      )}
      {token && (urls ? (
        <>
          <label>{t("Mein Kalender", "My calendar")}
            <input readOnly value={urls.mein} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <label>{t("Team-Kalender", "Team calendar")}
            <input readOnly value={urls.team} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <small className="muted">{t("In Google Kalender: „Weitere Kalender“ › „Per URL“ und den Link einfügen. Google lädt Abos nur alle paar Stunden neu. Die Links werden nur jetzt angezeigt.",
            "In Google Calendar: “Other calendars” › “From URL” and paste the link. Google refreshes subscriptions only every few hours. The links are only shown now.")}</small>
          <div className="row">
            <button type="button" onClick={() => void navigator.clipboard?.writeText(urls.mein)}>{t("Eigenen Link kopieren", "Copy own link")}</button>
            <button type="button" onClick={() => void navigator.clipboard?.writeText(urls.team)}>{t("Team-Link kopieren", "Copy team link")}</button>
          </div>
        </>
      ) : (
        <p className="notice">{t("Demo: Der Link ist erzeugt, aber in der Demo gibt es keinen Abo-Server.", "Demo: the link was created, but the demo has no subscription server.")}</p>
      ))}
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} onClick={erzeugen}>{stand.data ? t("Neuen Link erzeugen", "Create new link") : t("Link erzeugen", "Create link")}</button>
        {stand.data && (
          <button type="button" disabled={!!busy} onClick={() => void run("abo-ende", async () => { await aboBeenden(api); setToken(null); }, t("Abo beendet. Die Links gelten nicht mehr.", "Subscription ended. The links no longer work."))}>
            {t("Abo beenden", "End subscription")}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Kachel „Heute“ auf der Startseite ─────────────────────────────────────────

/** Startseite der Konsole: nächste Calls, fällige Aufgaben, Abwesenheiten und Kundenfristen; Vertrieb nur eigene, Admin und Entwicklung das Team */
export function HeuteKachel() {
  const betrieb = istBetrieb(useKonsolenRolle());
  const heuteTag = today(ZONE);
  const sicht: Sicht = betrieb ? "team" : "mein";
  const q = useQuery({ queryKey: [FEED_KEY, "heute", heuteTag, sicht], refetchInterval: 5 * 60_000,
    queryFn: () => ladeFeed(api, addDays(heuteTag, -30), addDays(heuteTag, 7), sicht) });
  // Praxistest 04.11.2026: Kundenfristen wie im Kalender aus der Team-Sicht (im Vertrieb eigene und solche ohne Zuständigen)
  const qFristen = useQuery({ queryKey: [FEED_KEY, "heute-fristen", heuteTag], refetchInterval: 5 * 60_000, enabled: !betrieb,
    queryFn: () => ladeFeed(api, heuteTag, addDays(heuteTag, 3), "team") });
  const ich = useIch() ?? "";
  const navigate = useNavigate();
  if (q.error) return <Fehler error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Laden />;
  const feed = q.data;
  const l = heuteListen(feed.eintraege, feed.heute, new Date());
  if (!betrieb && qFristen.data) l.fristen = kundenfristen(qFristen.data.eintraege, feed.heute, ich);
  const geh = (e: KalEintrag) => {
    const z = ziel(e);
    navigate(z && !istEigener(e) ? z.to : `/intern/kalender?ansicht=tag&datum=${e.starts_on}${betrieb ? "&sicht=team" : ""}`);
  };
  const liste = (es: KalEintrag[], leer: string, mitZeit: boolean) => es.length === 0 ? <p className="muted">{leer}</p> : (
    <ul className="tk-betrieb-liste">
      {es.map((e) => (
        <li key={e.key} className="tk-start-zeile">
          <span>
            <button type="button" className="tk-cal-link" onClick={() => geh(e)}><Punkt farbe={personFarbe(feed.personen, e.user_id)} /> {titel(e)}</button><br />
            <small>{mitZeit ? `${e.starts_on === feed.heute ? t("heute", "today") : date(e.starts_on)}${e.start_time ? ` · ${e.start_time}` : ""}` : e.starts_on < feed.heute ? t(`seit ${date(e.starts_on)}`, `since ${date(e.starts_on)}`) : t("heute", "today")}
              {betrieb && e.user_id ? ` · ${personName(feed.personen, e.user_id)}` : ""}</small>
          </span>
        </li>
      ))}
    </ul>
  );
  return (
    <section className="panel stack tk-kal-kachel" aria-labelledby="start-heute">
      <div className="row between">
        <h2 id="start-heute">{t("Heute", "Today")} · {tagLang(feed.heute)}</h2>
        <Link to={`/intern/kalender${betrieb ? "?sicht=team" : ""}`}>{t("Zum Kalender", "Open calendar")}</Link>
      </div>
      <div className="grid three tk-start-raster">
        <div className="stack">
          <h3>{betrieb ? t("Nächste Calls im Team", "Next calls in the team") : t("Deine nächsten Calls", "Your next calls")}</h3>
          {liste(l.calls, t("Keine Calls in den nächsten 7 Tagen.", "No calls in the next 7 days."), true)}
          {l.termine.length > 0 && (
            <p className="muted tk-kal-termine-heute">
              <Link to={`/intern/kalender?ansicht=tag&datum=${feed.heute}${betrieb ? "&sicht=team" : ""}`}>
                {l.termine.length === 1 ? t("Heute außerdem 1 Termin", "Also today: 1 appointment") : t(`Heute außerdem ${l.termine.length} Termine`, `Also today: ${l.termine.length} appointments`)}
              </Link>
            </p>
          )}
        </div>
        <div className="stack">
          <h3>{t("Fällige Aufgaben", "Tasks due")} · {l.aufgaben.length}</h3>
          {liste(l.aufgaben, t("Nichts fällig.", "Nothing due."), false)}
        </div>
        <div className="stack">
          <h3>{t("Abwesend und Fristen", "Away and deadlines")}</h3>
          {l.abwesend.length === 0 && l.fristen.length === 0 ? <p className="muted">{t("Heute niemand abwesend, keine Frist heute und in den nächsten 3 Tagen.", "Nobody away today, no deadline today or in the next 3 days.")}</p> : (
            <ul className="tk-betrieb-liste">
              {l.abwesend.map((e) => (
                <li key={e.key} className="tk-start-zeile"><span><Punkt farbe={personFarbe(feed.personen, e.user_id)} /> {personName(feed.personen, e.user_id)} · {titel(e)}<br />
                  <small>{zeitText(e)}</small></span></li>
              ))}
              {l.fristen.map((e) => (
                <li key={e.key} className="tk-start-zeile"><span><button type="button" className="tk-cal-link" onClick={() => geh(e)}>{titel(e)}</button><br />
                  <small>{e.starts_on === feed.heute ? t("heute", "today") : date(e.starts_on)}</small></span></li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
